import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addTextbox,
  addTextTemplate,
  getActiveEffects,
  updateEffectsParams
} from 'modules/effects';
import TextSidebar from '@picsart/rc/old/TextSidebar';
import { DragDropContext } from 'react-beautiful-dnd';
import { getCurrentTextId } from 'modules/currentTextId';
import {
  ITextData,
  IFontItem,
  IPackageItem
} from '@picsart/rc/types/containerComponents/Sidebar/types';
import { loadFont } from 'utils/textTool';
import { getSceneByTimeline } from 'modules/scenes';
import { getLines } from 'utils/konva';
import { isEmpty } from 'ramda';
import { Effect } from 'types/Effects';
import analytics from 'utils/analytics';
import { noop } from 'utils/noop';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';
import { getPremiumData, showCheckoutModal } from 'modules/checkoutModal';
import { getUser } from 'modules/user';
import { getFontFamily, getRecentlyUsedFonts, uploadFonts } from './utils';
import { addAlert } from 'modules/alert';
import { effectMessages } from 'constants/errorMessages';
import Modal from 'shared/Modal';
import { useTranslator } from '@picsart/rc/services/localization';
import { Text, Flex, Button, Box } from 'theme-ui';
import { ITextStyle } from '@picsart/rc/types/utils/types';
import { usePlayer } from 'utils/PlayerContext';
import ErrorLogger from '../ErrorLogger';

export const TextTool = () => {
  const dispatch = useDispatch();
  const player = usePlayer();
  const currentTextId = useSelector(getCurrentTextId);
  const effects = useSelector(getActiveEffects);
  const premiumItems = useSelector(getPremiumData);
  const sceneByTimeline = useSelector(getSceneByTimeline(player));
  const sceneId = sceneByTimeline?.id;
  const user = useSelector(getUser);
  const [recentlyUsedFonts, setRecentlyUsedFonts] = useState();

  useEffect(() => {
    if (user) {
      const userFonts = getRecentlyUsedFonts(user.key);
      setRecentlyUsedFonts(userFonts);
    }
  }, [user]);

  const handleAddText = (textData: ITextData) => {
    analytics.textAction();
    dispatch(addTextbox(textData));
  };

  const handleAddTextTemplate = (presetsData: IPackageItem) => {
    analytics.textPresetAction();
    dispatch(addTextTemplate(presetsData));
  };
  const handleFontChange = async (fontItem: IFontItem & { data_url?: string }) => {
    if (!sceneId) return;
    if (!currentTextId || isEmpty(effects)) {
      dispatch(
        addTextbox({
          name: 'addBodyText',
          text: 'Click to edit',
          fontItem: { ...fontItem, isPremium: fontItem.isPremium ?? fontItem.is_monotype }
        })
      );
      return;
    }
    if (isEmpty(currentTextId) || isEmpty(effects) || !sceneId) return;

    const font = {
      fontFamily: fontItem.meta_data.font_name,
      src: fontItem.data_url || fontItem.url,
      isPaid: fontItem.isPremium ?? fontItem.is_monotype
    };
    await loadFont(font);
    const effectParams = effects.map(effect => {
      const { fontSize, id } = effect;
      const textArr = getLines({ ...effect }, fontItem.meta_data.font_name);
      return {
        id,
        font,
        textArr,
        height: textArr.length * fontSize,
        presetId: null
      };
    });
    dispatch(updateEffectsParams(sceneId, effectParams));
    dispatch(saveRevision(ToolName.Text));
    analytics.fontCollectionsAction();
  };
  const activeFontSize = useMemo(
    () => effects?.reduce((acc: number, { fontSize }: Effect) => Math.min(acc, fontSize), Infinity),
    [effects]
  );
  const effectWithMinFontSize = useMemo(
    () => effects?.find(({ fontSize }: Effect) => fontSize === activeFontSize),
    [effects, activeFontSize]
  );

  const handleFileUpload = useCallback(
    async (files: File[], currentFonts: string[]) => {
      try {
        if (!user.key) {
          return;
        }
        await uploadFonts(files, currentFonts, user.key);
        setRecentlyUsedFonts(getRecentlyUsedFonts(user.key));
        return true;
      } catch (error) {
        ErrorLogger.trigger(error);
        dispatch(addAlert(effectMessages.fontUploadingTitle, effectMessages.fontUpoading));
      }
    },
    [user.key, dispatch]
  );
  const handleSelectTextStyle = useCallback(async (textSettings: ITextStyle) => {
    const { fontFamily } = await getFontFamily(textSettings.font_resource.resource_url);

    dispatch(
      addTextbox({
        name: 'addStyledText',
        text: textSettings.text_settings.title,
        fontItem: {
          id: textSettings.font_resource.resource_id,
          url: textSettings.font_resource.resource_url,
          isPremium: true,
          meta_data: {
            font_name: fontFamily
          },
          is_monotype: false,
          preview_url: ''
        },
        textSettings: { shadow_blur: 0.2, ...textSettings.text_settings }
      })
    );
  }, []);
  return (
    <DragDropContext onDragEnd={noop}>
      <TextSidebar
        hideTabs
        handleDropText={noop}
        addSelectedText={handleAddText}
        addNewFonts={handleFontChange}
        activeFontName={effectWithMinFontSize?.font?.fontFamily || ''}
        addTextTemplates={handleAddTextTemplate}
        hideRotates={true}
        handleUploadBtnClick={noop}
        handleFileUpload={handleFileUpload}
        selectTextStyle={handleSelectTextStyle}
        openMonetizationPopup={() => {
          dispatch(showCheckoutModal('gold_icon', premiumItems));
        }}
        confirmModal={({ show, handleCallback }) => {
          return <ConfirmModal show={show} handleCallback={handleCallback} />;
        }}
        recentlyUsedFonts={recentlyUsedFonts}
      />
    </DragDropContext>
  );
};

const ConfirmModal = ({
  show,
  handleCallback
}: {
  show: boolean;
  handleCallback: (result: boolean) => void;
}) => {
  const [isOpen, setIsopen] = useState(show);
  useEffect(() => {
    setIsopen(show);
  }, [show]);
  const translator = useTranslator();
  return (
    <Modal
      opened={isOpen}
      color="transparent"
      closeHandler={() => {
        handleCallback(false);
        setIsopen(false);
      }}
    >
      <Box sx={{ width: 420 }}>
        <Text sx={{ fontSize: 3, fontWeight: 'bold', textAlign: 'left' }} variant="body">
          {translator('Are you sure?')}
        </Text>
        <Text sx={{ fontSize: 2, mt: 5, textAlign: 'left' }} variant="body">
          {translator('Do you want to delete this font')}
        </Text>

        <Flex sx={{ justifyContent: 'flex-end' }}>
          <Button
            sx={{ mr: 1 }}
            onClick={() => {
              handleCallback(true);
              setIsopen(false);
            }}
            variant="clean"
          >
            {translator('Yes')}
          </Button>
          <Button
            onClick={() => {
              handleCallback(false);
              setIsopen(false);
            }}
          >
            {translator('No')}
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};
